import { useMemo, useRef, useState } from 'react';
import { IconCopy } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import {
    getAllDateFormats,
    todayInputValue,
} from './dateFormatEngine';
import {
    copyTextToClipboard,
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';

const SECTIONS = [
    {
        id: 'gregorian',
        cardClass: 'border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/20',
        titleClass: 'text-amber-900 dark:text-amber-300',
    },
    {
        id: 'bangla',
        cardClass: 'border-teal-200 bg-teal-50 dark:border-teal-900/40 dark:bg-teal-950/20',
        titleClass: 'text-teal-900 dark:text-teal-300',
    },
    {
        id: 'hijri',
        cardClass: 'border-violet-200 bg-violet-50 dark:border-violet-900/40 dark:bg-violet-950/20',
        titleClass: 'text-violet-900 dark:text-violet-300',
    },
];

const dateInputClass = 'w-full max-w-sm rounded-xl border border-[#ECE8E4] bg-white px-4 py-3 text-[16px] font-semibold tracking-wide text-[#3D3833] outline-none transition-colors focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100';

function FormatCard({ label, value, onCopy, copyLabel }) {
    return (
        <div className="rounded-xl border border-[#ECE8E4] bg-white px-3.5 py-3 dark:border-zinc-700 dark:bg-zinc-900">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.05em] text-[#A8A099] dark:text-zinc-500">
                        {label}
                    </p>
                    <p className="mt-1 break-words text-[13.5px] font-semibold leading-relaxed text-[#1C1815] dark:text-zinc-100">
                        {value}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onCopy}
                    className="inline-flex flex-none items-center gap-1 rounded-lg border border-[#ECE8E4] px-2 py-1 text-[11px] font-semibold text-[#6B635C] transition-colors hover:border-sky-400/50 dark:border-zinc-700 dark:text-zinc-300"
                >
                    <IconCopy size={12} stroke={1.9} />
                    {copyLabel}
                </button>
            </div>
        </div>
    );
}

function FormatSection({ sectionId, title, subtitle, formats, cardClass, titleClass, t, onCopy }) {
    if (!formats.length) return null;

    return (
        <div className={`rounded-2xl border px-4 py-4 sm:px-5 ${cardClass}`}>
            <div className="mb-3.5">
                <h3 className={`text-[14px] font-bold ${titleClass}`}>{title}</h3>
                <p className="mt-0.5 text-[12px] text-[#6B635C] dark:text-zinc-400">{subtitle}</p>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
                {formats.map((format) => (
                    <FormatCard
                        key={`${sectionId}-${format.id}`}
                        label={t(`dateFormatText.formats.${sectionId}.${format.id}`)}
                        value={format.value}
                        copyLabel={t('common.copy')}
                        onCopy={() => onCopy(format.value)}
                    />
                ))}
            </div>
        </div>
    );
}

export default function DateFormatText() {
    const { t } = useTranslation();
    const usageRecordedRef = useRef(false);

    const [dateInput, setDateInput] = useState(todayInputValue);

    const { parsed, gregorian, bangla, hijri } = useMemo(
        () => getAllDateFormats(dateInput),
        [dateInput],
    );

    const hasData = parsed !== null;
    const hasOutput = hasData && (gregorian.length > 0 || bangla.length > 0 || hijri.length > 0);

    const recordUsageOnce = () => {
        if (usageRecordedRef.current) return;
        usageRecordedRef.current = true;
        recordToolUsage('text/date-format', { category: 'text' });
    };

    const handleDateChange = (value) => {
        setDateInput(value);
        if (value) recordUsageOnce();
    };

    const handleCopy = (text) => {
        if (!text) return;

        if (copyTextToClipboard(text)) {
            notifications.show({
                title: t('common.success'),
                message: t('dateFormatText.copied'),
                color: 'green',
            });
            return;
        }

        void navigator.clipboard?.writeText(text)
            .then(() => {
                notifications.show({
                    title: t('common.success'),
                    message: t('dateFormatText.copied'),
                    color: 'green',
                });
            })
            .catch(() => {
                notifications.show({
                    title: t('common.error'),
                    message: t('dateFormatText.copyFailed'),
                    color: 'red',
                });
            });
    };

    const handleReset = () => {
        setDateInput(todayInputValue());
        usageRecordedRef.current = false;
    };

    const status = deriveToolStatus({
        hasInput: hasData,
        canRun: hasData,
        hasResult: hasOutput,
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`dateFormatText.howTo.step${i}`)),
        [t],
    );

    const statusText = getToolStatusText(status, t, {
        ready: t('dateFormatText.statusReady'),
        success: t('dateFormatText.statusSuccess'),
    });

    const sectionData = {
        gregorian,
        bangla,
        hijri,
    };

    return (
        <ToolPageLayout
            slug="text/date-format"
            subtitleKey="dateFormatText.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('dateFormatText.inputTitle')}
            howToSteps={howToSteps}
            onReset={dateInput !== todayInputValue() ? handleReset : undefined}
            controls={(
                <ToolCard title={t('dateFormatText.pickDateTitle')}>
                    <p className="mb-3 text-[12px] leading-relaxed text-[#6B635C] dark:text-zinc-400">
                        {t('dateFormatText.pickDateHint')}
                    </p>
                    <input
                        type="date"
                        value={dateInput}
                        onChange={(e) => handleDateChange(e.target.value)}
                        className={dateInputClass}
                    />
                </ToolCard>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('dateFormatText.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('dateFormatText.previewEmpty')}
                    idleHint={t('dateFormatText.previewEmptyHint')}
                    showIdle={!hasData}
                >
                    {hasData ? (
                        <div className="flex h-full min-h-0 flex-col justify-center gap-3 p-2 text-center">
                            <p className="text-[14px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                {t('dateFormatText.selectedDate')}
                            </p>
                            <p className="text-[20px] font-bold text-amber-700 dark:text-amber-300">
                                {dateInput}
                            </p>
                            <p className="text-[12.5px] text-[#6B635C] dark:text-zinc-400">
                                {t('dateFormatText.previewHint')}
                            </p>
                        </div>
                    ) : null}
                </ToolPreviewPanel>
            )}
            belowWorkArea={hasData ? (
                <div className="space-y-4">
                    {SECTIONS.map((section) => (
                        <FormatSection
                            key={section.id}
                            sectionId={section.id}
                            title={t(`dateFormatText.sections.${section.id}.title`)}
                            subtitle={t(`dateFormatText.sections.${section.id}.subtitle`)}
                            formats={sectionData[section.id]}
                            cardClass={section.cardClass}
                            titleClass={section.titleClass}
                            t={t}
                            onCopy={handleCopy}
                        />
                    ))}
                </div>
            ) : null}
        />
    );
}
